/**
 * Adapter: shared `TimeRangeValue` → the email-analytics wire format.
 *
 * Every `/api/admin/email-analytics/*` route parses the SAME `period` grammar —
 * `today`, `<N>d` (1–365), or `custom` with `from`&`to` as `YYYY-MM-DD` (the
 * routes also accept `start`/`end` as aliases). The canonical preset tokens
 * (`today`, `3d`, `7d`, `14d`, `30d`, `90d`) are already valid `period` values,
 * so the only real work is appending the two dates when the operator picked a
 * custom span.
 *
 * Kept in one place so the eight Measure/Marketing screens that speak this
 * dialect cannot drift apart — the failure mode where two halves of one feature
 * are built against slightly different keys.
 */

import { TimeRangeValue, resolveRangeYmd, toApiToken } from '../../common/timeRange';

/** `period=…` plus `from`&`to` when the range is custom. */
export function emailAnalyticsParams(value: TimeRangeValue): URLSearchParams {
  const params = new URLSearchParams({ period: toApiToken(value) });
  if (value.type === 'custom') {
    const { from, to } = resolveRangeYmd(value);
    params.set('from', from);
    params.set('to', to);
  }
  return params;
}

/** The same thing as a query string, without the leading `?`. */
export function emailAnalyticsQuery(value: TimeRangeValue): string {
  return emailAnalyticsParams(value).toString();
}

/**
 * The same split, as plain fields — for the typed `emailAnalyticsApi` helpers,
 * which build their own URLs.
 */
export function emailAnalyticsPeriod(value: TimeRangeValue): {
  period: string;
  dates?: { from: string; to: string };
} {
  if (value.type === 'custom') {
    return { period: 'custom', dates: resolveRangeYmd(value) };
  }
  return { period: toApiToken(value) };
}
